import { ZfLoadingSpinner, ZfStack } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { getImageUrlByString } from '../../styles/assets/imageMap';
import { useClient, useEvents } from '../api';
import { AddEventNeedButton } from '../components/lifeplan/AddEventNeedButton';
import { CompletedEventsToggle } from '../components/roadmap/CompletedEventsToggle';
import { RoadmapList } from '../components/roadmap/presentation/RoadmapList';
import { RoadmapTable } from '../components/roadmap/RoadmapTable';
import { RoadmapContextProvider } from '../contexts/roadmap-page-context';
import { useYwuApp } from '../contexts/ywu-app-context';
import { useContainerDimensions } from '../hooks/useContainerDimensions';
import type { RoadmapEvent } from '../interfaces/event';
import { saveReminderFile } from '../utils/calendarUtils';

export const RoadmapScreen: FunctionComponent = () => {
    const { t, i18n } = useTranslation();
    const { isPresentationMode } = useYwuApp();
    const containerDimensions = useContainerDimensions();

    const { data: client } = useClient();
    const { data: events } = useEvents();
    const allEvents = [...events.lifeEvents, ...events.maturities, ...events.advisoryNeedTasks];

    const handleEventReminderClick = (roadmapEvent: RoadmapEvent) => {
        saveReminderFile(roadmapEvent, t(roadmapEvent.eventType ?? ''), roadmapEvent.topicName?.[i18n.language] ?? '', client?.fullName ?? '');
    };

    return (
        <RoadmapContextProvider>
            <Suspense fallback={<ZfLoadingSpinner />}>
                {isPresentationMode ? (
                    <ZfStack direction="row" style={{ width: '100%', height: '100%' }}>
                        <ZfStack direction="row" style={{ flex: 1, backgroundColor: 'var(--bg-color-default-normal)' }}>
                            <ZfStack
                                direction="column"
                                spacing="extra-extra-large"
                                style={{
                                    flex: 1,
                                    padding: 'var(--padding-right-3xl) var(--padding-right-xl) 0 var(--padding-left-xl)',
                                    maxHeight: `calc(${containerDimensions.height}px - var(--padding-right-3xl))`,
                                    overflow: 'auto',
                                }}
                            >
                                <ZfStack direction="row" justify="space-between">
                                    <ZfStack direction="column" spacing="extra-small">
                                        <h4>{t('roadmapPresentationTitle')}</h4>
                                        <span style={{ color: 'var(--fg-color-default-normal)' }}>{t('roadmapPresentationSubtitle')}</span>
                                    </ZfStack>
                                    <ZfStack direction="row" alignment="center" style={{ marginTop: 'auto' }}>
                                        <CompletedEventsToggle />
                                        <AddEventNeedButton />
                                    </ZfStack>
                                </ZfStack>
                                <RoadmapList allEvents={allEvents} />
                            </ZfStack>
                        </ZfStack>
                        <div
                            style={{
                                background: `url(${getImageUrlByString('ROADMAP')})`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center',
                                backgroundSize: 'cover',
                                width: '480px',
                            }}
                        />
                    </ZfStack>
                ) : (
                    <ZfStack direction="column" spacing="extra-extra-large" className="roadmap-screen">
                        <ZfStack direction="row" justify="space-between" style={{ flexShrink: 0 }}>
                            <h4>{t('yourClientRoadmap')}</h4>
                            <ZfStack direction="row" justify="center">
                                <CompletedEventsToggle />
                                <AddEventNeedButton />
                            </ZfStack>
                        </ZfStack>
                        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                            <RoadmapTable allEvents={allEvents} onEventReminderClick={handleEventReminderClick} />
                        </div>
                    </ZfStack>
                )}
            </Suspense>
        </RoadmapContextProvider>
    );
};
